/**
 * RoomReasonCapture — say why, in the room, as it is said (host console P3;
 * Brief K, Doc 21 §6.4). The host or any participant picks an item, says
 * whether it is a reason, a dissent or a flag, and writes a line. It lands in
 * the session's Deliberation Record as an item rationale note (the one write,
 * `addAnnotation`), so it shows on the result beside the item it is about.
 * Optional by canon: never blocks answering or closing.
 */
import { useState } from 'react';
import { useDeliberationStore } from '@/stores/prioritization/deliberationStore';

export type RoomReasonKind = 'reason' | 'dissent' | 'flag';

const KIND_WORDS: Record<RoomReasonKind, string> = {
  reason: 'Reason',
  dissent: 'Dissent',
  flag: 'Flag',
};

/** The note as it is stored: the kind first, so the record reads as it was said. */
function roomReasonContent(kind: RoomReasonKind, text: string): string {
  return `${KIND_WORDS[kind]}: ${text.trim()}`;
}

export interface RoomReasonCaptureProps {
  sessionId: string;
  workspaceId: string;
  authorId: string;
  /** The room's items, by name. */
  items: Array<{ id: string; name: string; sourceId?: string }>;
}

export function RoomReasonCapture({ sessionId, workspaceId, authorId, items }: RoomReasonCaptureProps) {
  const addAnnotation = useDeliberationStore((s) => s.addAnnotation);
  const [open, setOpen] = useState(false);
  const [itemId, setItemId] = useState('');
  const [kind, setKind] = useState<RoomReasonKind>('reason');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(0);

  if (!workspaceId || !authorId || items.length === 0) return null;

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="room-reason-open"
        className="mt-3 w-full min-h-[40px] border border-[var(--rule-bright)] font-heading text-[0.7rem] tracking-[0.06em] uppercase text-[var(--text-sec)] hover:bg-[var(--hover-tint)]"
      >
        Say why{saved > 0 ? ` · ${saved} noted` : ''}
      </button>
    );
  }

  const save = async () => {
    if (!itemId || !text.trim() || busy) return;
    setBusy(true);
    setError(null);
    // A workspace item is noted under its uuid; an option typed into the
    // session under its own id, which the record holds as text (migration 1003).
    const item = items.find((i) => i.id === itemId);
    const result = await addAnnotation({
      sessionId,
      workspaceId,
      itemId: item?.sourceId ?? itemId,
      authorId,
      content: roomReasonContent(kind, text),
    });
    setBusy(false);
    if (!result.ok) {
      setError(result.error ?? 'It could not be noted.');
      return;
    }
    setSaved((n) => n + 1);
    setText('');
    setOpen(false);
  };

  return (
    <div className="mt-3 space-y-2 border border-[var(--rule)] p-3" data-testid="room-reason-form">
      <select
        value={itemId}
        onChange={(e) => setItemId(e.target.value)}
        aria-label="Which item"
        className="w-full border border-[var(--rule)] bg-[var(--bg-raised)] px-2 py-1.5 text-sm text-[var(--text-pri)]"
      >
        <option value="">Which item?</option>
        {items.map((i) => (
          <option key={i.id} value={i.id}>
            {i.name}
          </option>
        ))}
      </select>
      <div className="flex gap-1.5" role="group" aria-label="What it is">
        {(Object.keys(KIND_WORDS) as RoomReasonKind[]).map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setKind(k)}
            aria-pressed={kind === k}
            className={`flex-1 px-2 py-1 text-xs ${kind === k ? 'bg-[var(--slate)] text-white' : 'border border-[var(--rule)] text-[var(--text-sec)]'}`}
          >
            {KIND_WORDS[k]}
          </button>
        ))}
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={2}
        placeholder="What was said, in a line."
        className="w-full border border-[var(--rule)] bg-[var(--bg-raised)] px-2 py-1.5 text-sm text-[var(--text-pri)]"
      />
      {error && <p role="alert" className="text-xs text-[var(--coral)]">{error}</p>}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => void save()}
          disabled={busy || !itemId || !text.trim()}
          className="flex-1 px-2 py-1.5 text-xs font-medium text-white bg-[var(--slate)] disabled:opacity-50"
        >
          {busy ? 'Noting…' : 'Note it'}
        </button>
        <button type="button" onClick={() => setOpen(false)} className="px-2 py-1.5 text-xs text-[var(--text-sec)]">
          Cancel
        </button>
      </div>
      <p className="text-[0.7rem] text-[var(--text-ter)]">It goes on the session’s record, beside the item, under your name.</p>
    </div>
  );
}
